import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Cookies from 'universal-cookie';
import axios from 'axios';
import { API_BASE_URL, axiosInstance } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import { startGoogleSignIn } from '../utils/googleAuth';
import '../styles/auth.css';

const cookies = new Cookies();

const OAuthAuthorize = () => {
    const [searchParams] = useSearchParams();

    const redirectUri = searchParams.get('redirect_uri');
    const codeChallenge = searchParams.get('code_challenge');
    const codeChallengeMethod = searchParams.get('code_challenge_method') || 'S256';
    const state = searchParams.get('state');
    const clientId = searchParams.get('client_id');
    const resource = searchParams.get('resource');

    const [mode, setMode] = useState('loading');
    const [usernameOrEmail, setUsernameOrEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const getToken = () => cookies.get('access_token') || localStorage.getItem('auth_token');

    const handleGoogleSignIn = () => {
        startGoogleSignIn(`${window.location.pathname}${window.location.search}`);
    };

    const issueCodeAndRedirect = useCallback(async (overrideToken) => {
        setMode('authorizing');
        setError('');
        try {
            const token = overrideToken || getToken();
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const { data } = await axiosInstance.post(
                '/oauth/authorize',
                {
                    redirect_uri: redirectUri,
                    code_challenge: codeChallenge,
                    code_challenge_method: codeChallengeMethod,
                    state,
                    client_id: clientId,
                    resource: resource || undefined,
                },
                { headers }
            );

            const params = new URLSearchParams({ code: data.code });
            if (state) params.set('state', state);
            window.location.href = `${redirectUri}?${params.toString()}`;
        } catch (err) {
            const status = err.response?.status;
            if (status === 401 || status === 403) {
                cookies.remove('access_token', { path: '/' });
                localStorage.removeItem('auth_token');
                localStorage.removeItem('userID');
                setMode('login');
                return;
            }
            setError(err.response?.data?.error_description || err.response?.data?.message || 'Authorization failed');
            setMode('error');
        }
    }, [redirectUri, codeChallenge, codeChallengeMethod, state, clientId, resource]);

    useEffect(() => {
        if (!redirectUri || !codeChallenge) {
            setMode('bad_params');
            return;
        }

        if (getToken()) {
            issueCodeAndRedirect();
            return;
        }

        setMode('login');
    }, [redirectUri, codeChallenge, issueCodeAndRedirect]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setMode('authorizing');
        try {
            const { data } = await axios.post(`${API_BASE_URL}/auth/login`, {
                usernameOrEmail,
                password,
            });
            cookies.set('access_token', data.token, { path: '/', maxAge: 60 * 60 * 24 * 7 });
            localStorage.setItem('auth_token', data.token);
            localStorage.setItem('userID', data.userID);
            localStorage.setItem('username', data.username);
            if (data.preferredRegion) {
                localStorage.setItem('preferredRegion', data.preferredRegion);
            }
            window.dispatchEvent(new CustomEvent('auth-token-updated'));
            await issueCodeAndRedirect(data.token);
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed');
            setMode('login');
        }
    };

    if (mode === 'loading' || mode === 'authorizing') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <LoadingSpinner message={mode === 'authorizing' ? 'Connecting AI assistant…' : 'Loading…'} />
            </div>
        );
    }

    if (mode === 'bad_params') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
                <div className="max-w-md text-center">
                    <h1 className="text-xl font-semibold text-gray-900 mb-2">Invalid connection request</h1>
                    <p className="text-gray-600">Missing OAuth parameters. Start the connection from your AI assistant.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
            <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">Connect to What To Make</h1>
                <p className="text-gray-600 mb-6 text-sm">
                    Authorize your AI assistant to manage recipes, shopping lists, and pricing on your account.
                </p>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
                )}

                {mode === 'error' ? (
                    <div>
                        <button
                            type="button"
                            onClick={() => setMode('login')}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-lg mb-3"
                        >
                            Try again
                        </button>
                        <Link to="/" className="block text-center text-sm text-gray-500 hover:text-gray-700">
                            Cancel
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={handleLogin}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email or username</label>
                        <input
                            type="text"
                            value={usernameOrEmail}
                            onChange={(e) => setUsernameOrEmail(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
                            required
                        />
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-6"
                            required
                        />
                        <button
                            type="submit"
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-lg"
                        >
                            Sign in &amp; connect
                        </button>
                        <div className="google-signin-container">
                            <p className="or-divider"><span>OR</span></p>
                            <button
                                type="button"
                                className="google-signin-btn"
                                onClick={handleGoogleSignIn}
                            >
                                <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google logo" />
                                Sign in with Google
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default OAuthAuthorize;
